import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Lightweight image slider for sections (not full-bleed hero).
 * Props:
 *   slides: [{ img, caption? }]
 *   aspect: tailwind aspect class, default 'aspect-[16/9]'
 *   testId: data-testid prefix
 */
export default function ImageSlider({
  slides,
  aspect = "aspect-[16/9]",
  testId = "image-slider",
  intervalMs = 4500,
}) {
  const [index, setIndex] = React.useState(0);
  const timerRef = React.useRef(null);

  const goTo = React.useCallback(
    (i) => setIndex((i + slides.length) % slides.length),
    [slides.length]
  );
  const next = React.useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = React.useCallback(() => goTo(index - 1), [index, goTo]);

  React.useEffect(() => {
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [slides.length, index, intervalMs]);

  return (
    <div
      data-testid={testId}
      className={`relative w-full ${aspect} rounded-sm overflow-hidden border border-wood-200 bg-wood-100`}
    >
      {slides.map((s, i) => (
        <div
          key={i}
          data-testid={`${testId}-slide-${i}`}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={s.img}
            alt={s.caption || `slide ${i + 1}`}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {s.caption && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-wood-900/85 to-transparent p-5 sm:p-7">
              <div className="text-xs uppercase tracking-[0.2em] text-gold-400 font-bold">
                {s.caption}
              </div>
            </div>
          )}
        </div>
      ))}

      <button
        data-testid={`${testId}-prev`}
        onClick={prev}
        aria-label="Previous slide"
        className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white/15 backdrop-blur text-white hover:bg-gold-500 hover:text-wood-900 transition-all items-center justify-center"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        data-testid={`${testId}-next`}
        onClick={next}
        aria-label="Next slide"
        className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white/15 backdrop-blur text-white hover:bg-gold-500 hover:text-wood-900 transition-all items-center justify-center"
      >
        <ChevronRight size={20} />
      </button>

      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {slides.map((_, i) => (
          <button
            key={i}
            data-testid={`${testId}-dot-${i}`}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-8 bg-gold-400" : "w-4 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
